import axios from "axios"

import APIReturnPage from "@/api/returnpage"

import generateHash from "./generateHash"

export type PromiseFn<T = any> = (...args: any[]) => Promise<T>
export interface UploadFilesToOssOptions {
  name?: string
}
export const uploadFilesToOss = async (
  files: File[],
  getOssInfo: PromiseFn<{ data: { data: any } }>,
  options?: UploadFilesToOssOptions,
) => {
  const {
    data: { data },
  } = await getOssInfo()
  const customFileName = options?.name ?? ""
  const { host, dir, policy, accessid, callback, signature, access_host } = data
  const tasks = files.map(async (file) => {
    const suffix = file.name.replace(/.*\./gi, "")
    const hash = await generateHash(file)
    const formData = new FormData()
    const data: any = {
      name: file.name,
      key: `${dir}${customFileName}${hash}.${suffix}`,
      policy: policy,
      OSSAccessKeyId: accessid,
      success_action_status: "200",
      "Content-Disposition": "inline",
      "x-oss-content-type": file.type,
      callback: callback,
      signature: signature,
      file: file,
      "x:md5": hash,
      "x:name": file.name,
    }
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key])
    })
    try {
      const res = await axios.post(host, formData)
      return {
        ...data,
        ossUrl:
          res.data.Status == "Ok"
            ? `${access_host}${dir}${customFileName}${hash}.${suffix}`
            : "",
        size: file.size,
        type: file.type,
      }
    } catch (error) {
      console.error(error)
      return {
        ...data,
      }
    }
  })
  return Promise.all(tasks)
}

export interface UploadFileAfterPutParams {
  outputUrl: string
  file: File
  key: string
}

export interface UploadFilesToR2Options {
  onAfterPut?: (params: UploadFileAfterPutParams) => Promise<unknown>
}

export interface UploadFileResult {
  ossUrl: string
  name: string
  size: number
  type: string
  path: string
  afterPutResult?: unknown
}

const createFailedUploadResult = (file: File, path = ""): UploadFileResult => ({
  ossUrl: "",
  name: file.name,
  size: file.size,
  type: file.type,
  path,
})

// 新版r2上传
export const uploadFilesToR2 = async (
  files: File[],
  options?: UploadFilesToR2Options,
): Promise<UploadFileResult[]> => {
  const tasks = files.map(async (file) => {
    try {
      const data: any = await APIReturnPage.getUploadKeyV2({
        filename: file.name,
      })
      const { url, key, output_url } = data.data.data

      const res = await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      })
      if (res.status !== 200) {
        return createFailedUploadResult(file, key)
      }

      const validatePromise = APIReturnPage.validateUploadKey({
        url: output_url,
      })
      const afterPutPromise =
        options?.onAfterPut?.({
          outputUrl: output_url,
          file,
          key,
        }) ?? Promise.resolve(undefined)

      const [res2, afterPutResult] = await Promise.all([
        validatePromise,
        afterPutPromise,
      ])

      if (res2.code !== 1000) {
        return createFailedUploadResult(file, key)
      }

      return {
        ossUrl: output_url,
        name: file.name,
        size: file.size,
        type: file.type,
        path: key,
        afterPutResult,
      }
    } catch (error) {
      return createFailedUploadResult(file)
    }
  })
  return Promise.all(tasks)
}

export const validPage = ({ total, pageSize, current }) => {
  const maxPageNo = Math.ceil(total / pageSize)
  return {
    canNext: current < maxPageNo,
    canPre: current > 1,
  }
}
